<script setup lang="ts">
  import { computed, PropType, useTemplateRef } from 'vue'
  import { AuditLog } from '@/common/graphql/types'
  import AuditLogTimelineItem from '@/common/components/auditlog/components/AuditLogTimelineItem.vue'
  import MdiIcon from '@/common/components/svg/MdiIcon.vue'
  import { mdiCalendar } from '@mdi/js'
  import FormInput from '@/common/components/form/FormInput.vue'
  import ActionButton from '@/common/components/button/ActionButton.vue'
  import AuditLogDate from '@/common/components/auditlog/components/AuditLogDate.vue'
  import AuditLogHeader from '@/common/components/auditlog/components/AuditLogHeader.vue'
  import type { ComponentExposed } from 'vue-component-type-helpers'

  defineEmits(['setStartTime'])

  const props = defineProps({
    auditLogs: {
      type: Array as PropType<AuditLog[]>,
      required: true
    },
    hideDatePicker: {
      type: Boolean,
      required: false,
      default: false,
    },
  })

  const timeInput = useTemplateRef<ComponentExposed<typeof FormInput>[]>('timeInput')

  function showPicker () {
    if (timeInput.value && timeInput.value.length > 0) {
      timeInput.value[0].inputField?.focus()
      timeInput.value[0].inputField?.showPicker()
    }
  }

  const groupedByDate = computed(() => {
    return props.auditLogs.reduce((acc, log) => {
      const date = new Date(log.created_at)
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(log)
      return acc
    }, {} as Record<string, AuditLog[]>)
  })
</script>

<template>
  <div class="relative z-10">
    <div class="w-12 absolute top-20 bottom-0 flex justify-center">
      <div class="w-px bg-gray-200" />
    </div>
    <div class="space-y-16">
      <div v-for="(logs, date) in groupedByDate" :key="date" class="relative">
        <AuditLogHeader :key="date" class="top-4">
          <template #center>
            <AuditLogDate :date="date" @click="showPicker" />
          </template>
          <template #right>
            <ActionButton v-if="!hideDatePicker" displayMode="icon" @click.prevent="showPicker">
              <template #icon>
                <MdiIcon :icon="mdiCalendar" class="size-5" />
              </template>
            </ActionButton>
            <FormInput
              ref="timeInput"
              class="sr-only"
              name="start_time"
              type="date"
              @update:modelValue="$emit('setStartTime', $event)"
            />
          </template>
        </AuditLogHeader>
        <AuditLogTimelineItem v-for="log in logs" :key="log.id" class="mt-3" :log="log" />
      </div>
    </div>
  </div>
</template>
